import {
  formatPlayTestBooleanValue,
  getValueForMetaForPlayer,
  isPlayTestOutcomeValueClear,
  normalizePlayTestBooleanValue,
  parsePlayTestEnumTypes,
} from "@seabrookstudios/pitch2table-core";

export const PLAYER_MIX_SEGMENTS = [
  { key: "winnerAndFirst", label: "Winner and first" },
  { key: "winnerOnly", label: "Winner only" },
  { key: "firstOnly", label: "First only" },
  { key: "everyoneElse", label: "Everyone else" },
];

const emptySegmentCounts = () => ({
  winnerAndFirst: 0,
  winnerOnly: 0,
  firstOnly: 0,
  everyoneElse: 0,
});

/**
 * @param {{ won?: boolean, first?: boolean }} player
 * @returns {"winnerAndFirst"|"winnerOnly"|"firstOnly"|"everyoneElse"}
 */
export const classifyPlayerMixRole = (player) => {
  const won = !!player.won;
  const first = !!player.first;
  if (won && first) {
    return "winnerAndFirst";
  }
  if (won) {
    return "winnerOnly";
  }
  if (first) {
    return "firstOnly";
  }
  return "everyoneElse";
};

/**
 * @param {{ outcomes?: import("@seabrookstudios/pitch2table-core").PlayTestPlayer[] }} playTest
 * @returns {boolean}
 */
export const playTestHasWinnerOrFirst = (playTest) => {
  return (playTest.outcomes || []).some((player) => player.won || player.first);
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue} value
 * @returns {{ kind: "unknown" } | { kind: "choice", label: string }}
 */
export const classifyBooleanMixValue = (value) => {
  const normalized = normalizePlayTestBooleanValue(value);
  if (normalized === undefined) {
    return { kind: "unknown" };
  }
  return { kind: "choice", label: formatPlayTestBooleanValue(normalized) };
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue} value
 * @param {string[]} options
 * @returns {{ kind: "choice", label: string } | { kind: "unknown" } | { kind: "other", label: string }}
 */
export const classifyEnumMixValue = (value, options) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    isPlayTestOutcomeValueClear(value) ||
    typeof value !== "string"
  ) {
    return { kind: "unknown" };
  }

  const match = options.find((option) => option.toLocaleLowerCase() === value.toLocaleLowerCase());
  if (match) {
    return { kind: "choice", label: match };
  }

  return { kind: "other", label: value };
};

/**
 * Player observations from included play tests that have a winner or first player.
 *
 * @param {Array<{ ignore?: boolean, outcomes?: import("@seabrookstudios/pitch2table-core").PlayTestPlayer[] }>} playTests
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentId} metaId
 * @returns {{ value: import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue, role: ReturnType<typeof classifyPlayerMixRole> }[]}
 */
export const getIncludedPerPlayerMixObservations = (playTests, metaId) => {
  return playTests
    .filter((pt) => pt.ignore)
    .filter(playTestHasWinnerOrFirst)
    .flatMap((pt) =>
      (pt.outcomes || []).map((player) => ({
        value: getValueForMetaForPlayer({ ...player, scores: player.scores || [] }, metaId),
        role: classifyPlayerMixRole(player),
      }))
    );
};

/**
 * @param {Map<string, ReturnType<typeof emptySegmentCounts>>} countsByLabel
 * @param {string} label
 * @param {ReturnType<typeof classifyPlayerMixRole>} role
 */
const increment = (countsByLabel, label, role) => {
  if (!countsByLabel.has(label)) {
    countsByLabel.set(label, emptySegmentCounts());
  }
  const counts = countsByLabel.get(label);
  counts[role] += 1;
};

/**
 * @param {string[]} labels
 * @param {Map<string, ReturnType<typeof emptySegmentCounts>>} countsByLabel
 * @returns {{ label: string, winnerAndFirst: number, winnerOnly: number, firstOnly: number, everyoneElse: number }[]}
 */
const rowsFromLabels = (labels, countsByLabel) => {
  return labels.map((label) => ({
    label,
    ...(countsByLabel.get(label) || emptySegmentCounts()),
  }));
};

/**
 * @param {{ value: import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue, role: ReturnType<typeof classifyPlayerMixRole> }[]} observations
 * @returns {{ label: string, winnerAndFirst: number, winnerOnly: number, firstOnly: number, everyoneElse: number }[]}
 */
export const buildBooleanPlayerMixDataset = (observations) => {
  const labels = [formatPlayTestBooleanValue(true), formatPlayTestBooleanValue(false)];
  /** @type {Map<string, ReturnType<typeof emptySegmentCounts>>} */
  const countsByLabel = new Map(labels.map((label) => [label, emptySegmentCounts()]));

  observations.forEach((observation) => {
    const classified = classifyBooleanMixValue(observation.value);
    if (classified.kind === "unknown") {
      return;
    }
    increment(countsByLabel, classified.label, observation.role);
  });

  return rowsFromLabels(labels, countsByLabel);
};

/**
 * @param {{ value: import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue, role: ReturnType<typeof classifyPlayerMixRole> }[]} observations
 * @param {string|undefined} enumTypes
 * @returns {{ label: string, winnerAndFirst: number, winnerOnly: number, firstOnly: number, everyoneElse: number }[]}
 */
export const buildEnumPlayerMixDataset = (observations, enumTypes) => {
  const options = parsePlayTestEnumTypes(enumTypes);
  /** @type {Map<string, ReturnType<typeof emptySegmentCounts>>} */
  const countsByLabel = new Map(options.map((option) => [option, emptySegmentCounts()]));
  /** @type {string[]} */
  const otherLabels = [];

  observations.forEach((observation) => {
    const classified = classifyEnumMixValue(observation.value, options);
    if (classified.kind === "unknown") {
      return;
    }
    if (classified.kind === "other" && !countsByLabel.has(classified.label)) {
      otherLabels.push(classified.label);
    }
    increment(countsByLabel, classified.label, observation.role);
  });

  return rowsFromLabels([...options, ...otherLabels], countsByLabel);
};

/**
 * @param {{ value: import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValue, role: ReturnType<typeof classifyPlayerMixRole> }[]} observations
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataValueType|undefined} valueType
 * @param {string|undefined} [enumTypes]
 * @returns {{ label: string, winnerAndFirst: number, winnerOnly: number, firstOnly: number, everyoneElse: number }[]}
 */
export const buildPerPlayerMixDataset = (observations, valueType, enumTypes) => {
  if (valueType === "ENUM") {
    return buildEnumPlayerMixDataset(observations, enumTypes);
  }
  return buildBooleanPlayerMixDataset(observations);
};

/**
 * @param {{ winnerAndFirst: number, winnerOnly: number, firstOnly: number, everyoneElse: number }} row
 * @returns {number}
 */
export const playerMixRowTotal = (row) => {
  return row.winnerAndFirst + row.winnerOnly + row.firstOnly + row.everyoneElse;
};
